import { Alert, Box, Button, FileInput, Image, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTrash, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import {
  useBotImageDownloadUrl,
  useDeleteBotImage,
  useTenantConfig,
  useUploadBotImage,
} from 'src/secret-menu/hooks/useTenantConfig';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const MAX_FILE_SIZE_MB = 1.3;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type Props = {
  companyId: string;
};

export default function BotImageUpload({ companyId }: Props) {
  const { config, refetch } = useTenantConfig(companyId);
  const { uploadBotImage, isLoading: isUploading } = useUploadBotImage(companyId);
  const { deleteBotImage, isLoading: isDeleting } = useDeleteBotImage(companyId);
  const { downloadUrl, refetch: refetchDownloadUrl } = useBotImageDownloadUrl(
    companyId,
    !!config?.botImageS3Key,
  );
  const [botImageFile, setBotImageFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = (file: File | null): string | null => {
    if (!file) return null;

    const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
    if (!isJpeg) {
      return 'File must be a JPEG image.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File size (${sizeMB}MB) exceeds maximum allowed size of ${MAX_FILE_SIZE_MB}MB.`;
    }

    return null;
  };

  const handleFileChange = (file: File | null) => {
    const error = validateFile(file);
    setValidationError(error);
    setBotImageFile(error ? null : file);

    if (error) {
      notifications.show({
        title: 'Invalid file',
        message: error,
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleUploadBotImage = async () => {
    if (!botImageFile) return;

    const error = validateFile(botImageFile);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await uploadBotImage(botImageFile);
      setBotImageFile(null);
      setValidationError(null);
      await refetch();
      await refetchDownloadUrl();
    } catch (err) {
      notifyAndLogError(
        'Failed to upload bot image',
        'An error occurred while uploading the bot image.',
        err,
      );
    }
  };

  const handleDeleteBotImage = async () => {
    try {
      await deleteBotImage();
      await refetch();
    } catch (error) {
      notifyAndLogError(
        'Failed to delete bot image',
        'An error occurred while deleting the bot image.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <Box maw={800} mb="lg">
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
          Meeting Bot Image
        </Text>
        <Text
          size="textMd"
          fw={FONT_WEIGHTS.medium}
          mt="xxxs"
          c="gray.6"
          style={{ whiteSpace: 'pre-line' }}
        >
          {`Upload a JPEG image to display as the bot's avatar when it joins meetings.
The image should be 16:9 aspect ratio with recommended resolution of 1280x720.
Maximum file size: 1.3MB.`}
        </Text>
      </Box>

      {config?.botImageS3Key && downloadUrl && (
        <Box>
          <Image src={downloadUrl} alt="Bot avatar" maw={320} radius="md" mb="xs" />
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={handleDeleteBotImage}
            loading={isDeleting}
          >
            Remove
          </Button>
        </Box>
      )}

      <FileInput
        label="Bot Image"
        description="JPEG format only, max 1.3MB"
        placeholder="Click to select file"
        accept="image/jpeg,image/jpg"
        value={botImageFile}
        onChange={handleFileChange}
        leftSection={<IconUpload size={16} />}
        maw={400}
        error={validationError}
      />

      {validationError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {validationError}
        </Alert>
      )}

      <Button
        onClick={handleUploadBotImage}
        loading={isUploading}
        disabled={!botImageFile || !!validationError}
        leftSection={<IconUpload size={16} />}
        w="fit-content"
      >
        Upload Bot Image
      </Button>
    </Stack>
  );
}
